import React, { useState, useEffect } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  isSameMonth,
} from "date-fns";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import axios from "axios";
import dayjs from "dayjs";
import { useAtom } from "jotai";
import { gbState } from "../../../components/shared/GlobalState";
import toast from "react-hot-toast";

const EventListPublicChild = () => {
  const [{ errorLogApi, user }] = useAtom(gbState);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [data, setData] = useState([]);

  //Get Events data
  const getDataFromServer = () => {
    let userToken = localStorage.getItem("token");
    let urlLink = `${process.env.REACT_APP_API_URL}/public-event/${user.church_id}`;

    const config = {
      method: "GET",
      url: urlLink,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };

    axios(config)
      .then(function (response) {
        const oldData = response.data.events;
        const updatedData = oldData.map((data) => {
          return {
            ...data,
            id: data._id,
            title: data.title,
            description: data.description,
            event_start_date: data?.event_start_date,
            view_event_start_date: data?.event_start_date
              ? dayjs(data?.event_start_date).format("DD-MM-YYYY") +
                " " +
                data?.event_start_time
              : "",
            event_end_date: data?.event_end_date,
            view_event_end_date: data?.event_end_date
              ? dayjs(data?.event_end_date).format("DD-MM-YYYY") +
                " " +
                data?.event_end_time
              : "",
          };
        });

        setData(updatedData);
      })
      .catch(function (err) {
        console.log("error api call ", err);
        errorLogApi(err, "EventListChild", "/events");
      });
  };

  useEffect(() => {
    getDataFromServer();
  }, []);

  // Get all days for the calendar view (including padding days)
  const getDaysInMonth = (date) => {
    const start = startOfWeek(startOfMonth(date));
    const end = endOfWeek(endOfMonth(date));
    return eachDayOfInterval({ start, end });
  };

  const days = getDaysInMonth(currentDate);

  const nextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const previousMonth = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };

  // Add this helper function to get events for a specific date
  const getEventsForDate = (date) => {
    return data.filter((event) => {
      const eventDate = new Date(event.event_start_date);
      return (
        eventDate.getDate() === date.getDate() &&
        eventDate.getMonth() === date.getMonth() &&
        eventDate.getFullYear() === date.getFullYear()
      );
    });
  };

  return (
    <div className="p-4 w-full">
      {/* Calendar Header */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">
          {format(currentDate, "MMMM yyyy")}
        </h2>
        <div className="flex gap-2">
          <button
            onClick={previousMonth}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
          <button
            onClick={nextMonth}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {/* Week days header */}
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
          <div
            key={day}
            className="p-2 text-sm font-medium text-center text-gray-600"
          >
            {day}
          </div>
        ))}

        {/* Update the Calendar days section */}
        {days.map((day, index) => {
          const isCurrentMonth = isSameMonth(day, currentDate);
          const dayEvents = getEventsForDate(day);

          return (
            <div
              key={index}
              className={`border aspect-square p-2 min-h-[100px] hover:bg-gray-50 ${
                !isCurrentMonth ? "bg-gray-50" : ""}`}
            >
              <div
                className={`text-sm ${
                  isCurrentMonth ? "text-gray-600" : "text-gray-400"
                }`}
              >
                {format(day, "d")}
              </div>
              {/* Display actual events */}
              <div className="mt-1 space-y-1">
                {isCurrentMonth &&
                  dayEvents.map((event) => (
                    <div
                      key={event.id}
                      className="p-1 text-xs text-blue-800 truncate bg-blue-100 rounded cursor-pointer hover:bg-blue-200"
                      title={`${event.title}\n${event.view_event_start_date}`}
                    >
                      {event.title}
                    </div>
                  ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EventListPublicChild;
